import { useMemo } from 'react';
import { InfoSection } from './components/InfoSection';
import { Button, Row } from '../../../commonComponents';
import { formatBenefits, formatPayRange, formatPayTitle } from './utils/formatters';
import { EnumHelper } from '../../../utils/EnumHelper';
import { UserProfession, UserSubProfession } from '../../../enums/UserProfession';
import { IEditableFormProps } from './Types';
import { Colors } from '../../../themes/colors';
import { GeneralJobInfoForm, GeneralJobInfoPreview } from './components/GeneralJobInfo';
import { EducationUtils } from '~/utils/EducationUtils';

export const GeneralJobInfoEditable = ({
  jobPosting,
  editMode,
  hideEditButton,
  disableSave,
  control,
  errors,
  setValue,
  onEditClick,
  onCancelClick,
  onSaveClick,
  readonlyFields,
}: IEditableFormProps) => {
  const {
    benefits,
    benefitsOther,
    maxRate,
    minRate,
    officeName,
    officeSpecialty,
    payType,
    professionType,
    subProfession,
    title,
  } = jobPosting || {};

  // Format display values
  const payTitleDisplay = formatPayTitle(minRate, maxRate);
  const payRangeDisplay = formatPayRange(minRate, maxRate, payType);
  const benefitsDisplay = formatBenefits(benefits?.filter((it) => it !== 'Other') || []);

  // Get profession display name (subProfession takes precedence)
  const professionDisplay = useMemo(() => {
    const profToDisplay = subProfession || professionType;
    if (!profToDisplay) return '';

    const isSubProfession = EnumHelper.getObjectKeys(UserSubProfession).includes(profToDisplay);
    return isSubProfession
      ? UserSubProfession.getName(profToDisplay)
      : UserProfession.getName(profToDisplay);
  }, [professionType, subProfession]);

  if (editMode) {
    return (
      <InfoSection
        icon="briefcase-solid"
        title="General Job Information"
        rightAction={
          <Row gap={10}>
            <Button
              text="Cancel"
              onClick={onCancelClick}
              style={{ width: 90, height: 30, backgroundColor: Colors.secondary_500 }}
              textStyle={{ fontSize: 16 }}
            />
            <Button
              disabled={disableSave}
              text="Save"
              onClick={onSaveClick}
              style={{ width: 90, height: 30 }}
              textStyle={{ fontSize: 16 }}
            />
          </Row>
        }
      >
        <GeneralJobInfoForm
          jobPosting={jobPosting}
          control={control}
          errors={errors}
          setValue={setValue}
          readonlyFields={readonlyFields}
        />
      </InfoSection>
    );
  }

  return (
    <InfoSection
      icon="briefcase-solid"
      title="General Job Information"
      rightActionIcon="edit-pencil"
      onRightActionPress={hideEditButton ? undefined : onEditClick}
    >
      <GeneralJobInfoPreview
        officeName={officeName || ''}
        title={title || ''}
        professionType={professionDisplay || ''}
        specialty={EducationUtils.removeSpecialtyPrefix(officeSpecialty || '')}
        payType={payType || ''}
        payTitle={payTitleDisplay}
        payRange={payRangeDisplay}
        benefits={benefitsDisplay}
        benefitsOther={benefitsOther || ''}
      />
    </InfoSection>
  );
};
